import { Component, Injector, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { SweetAlertHelper } from 'app/core/helpers/sweet-alert-helper.service';
import { IBatchcredit, IBatchCreditInfo, ISeachTrip } from 'app/core/interfaces/posting.interface';
import { PostingService } from 'app/core/services/posting.service';
import { PayorsService } from 'app/pages/payors/payors.service';
import { T2LoadingService } from 'app/trip2-theme/components/t2-loading/t2-loading.service';
import { IMyOptions } from 'mydatepicker';
import { Subscription } from 'rxjs/Subscription';
import * as moment from 'moment';
import { getDateFormatted } from 'app/utils/helpers';
import { ParentModule } from 'app/pages/A2-utils/ParentClass/parent-module';
import { BatchStatusEnum } from 'app/core/enums/batch-status.enum';

@Component({
  selector: 'app-form-posting-batch',
  templateUrl: './form-posting-batch.component.html',
  styleUrls: ['./form-posting-batch.component.scss'],
})

export class FormPostingBatchComponent extends ParentModule implements OnInit, OnDestroy {
  subscriptions: Subscription[] = [];
  formPostingBatch: FormGroup;
  searchForm: FormGroup;
  arrayPayors: any[] = [];
  arrayBatchcreditid: number[] = [];
  batchinfoid: number;
  myDatePickerOptions: IMyOptions = {
    dateFormat: 'mm/dd/yyyy',
    editableDateField: true,
    firstDayOfWeek: 'su',
  };
  disabledFilter = true;
  disabledSorting = true;
  readOnlyMode = false;

  get checkdateCtrl(): AbstractControl { return this.formPostingBatch.get('checkdate'); }
  get checkdateDepCtrl(): AbstractControl { return this.formPostingBatch.get('depositdate'); }
  get descriptionCtrl(): AbstractControl { return this.formPostingBatch.get('description'); }
  get checkCtrl(): AbstractControl { return this.formPostingBatch.get('check'); }
  get btnFloppyIsValid(): boolean {
    return !this.descriptionCtrl.valid || !this.checkCtrl.valid || this.readOnlyMode;
  }

  get btnPostBatchIsValid(): boolean {
    return !this.formPostingBatch.valid || !this.batchinfoid || this.readOnlyMode || this.remaining !== 0;
  }

  get titleForm(): string {
    return !this.formPostingBatch.get('batchinfoid').value
      ? 'New Batch'
      : `Batch #${this.formPostingBatch.get('batchinfoid').value}`;
  }

  get isEdit(): boolean {
    return this.formPostingBatch.get('batchinfoid').value;
  }

  get isPosting(): boolean {
    return this.formPostingBatch.get('isposting').value || !this.formPostingBatch.valid;
  }

  get batchCredits(): FormArray {
    return this.formPostingBatch.get('batchcredits') as FormArray;
  }

  get disabledSearchBtn(): boolean {
    return !this.searchForm.get('search').value || this.readOnlyMode;
  }

  get writeoffamount(): number {
    const value = this.formPostingBatch.get('writeoffamount').value;
    return value ? value : 0;
  }

  get postedamount(): number {
    const value = this.formPostingBatch.get('postedamount').value;
    return value ? value : 0;
  }

  get revenueadjustments(): number {
    const value = this.formPostingBatch.get('revenueadjustments').value;
    return value ? value : 0;
  }

  get contractualallowance(): number {
    const value = this.formPostingBatch.get('contractualallowance').value;
    return value ? value : 0;
  }

  get refunds(): number {
    const value = this.formPostingBatch.get('refunds').value;
    return value ? value : 0;
  }

  get remaining(): number {
    const totaltarget = this.formPostingBatch.get('totaltarget').value;
    const posted = this.formPostingBatch.get('postedamount').value;
    const refunds = this.formPostingBatch.get('refunds').value;

    let resultOp = 0;
    if (!isNaN((totaltarget - posted))) {
      resultOp = ((posted + refunds) - totaltarget);
    }

    return Number(resultOp.toFixed(2));
  }

  get batchStatusIdCtrl(): AbstractControl {
    return this.formPostingBatch.get('batchstatusid');
  }

  get disabledDeleteCredit(): boolean {
    return !this.arrayBatchcreditid.length || this.readOnlyMode;
  }

  get inValidCheckDate(): boolean {
    return !this.checkdateCtrl.valid && !this.readOnlyMode;
  }

  get inValidDepositDate(): boolean {
    return !this.checkdateDepCtrl.valid && !this.readOnlyMode;
  }

  constructor(
    private fb: FormBuilder,
    private postingService: PostingService,
    private payorService: PayorsService,
    private sweetAlertHelper: SweetAlertHelper,
    private t2LoadingService: T2LoadingService,
    protected inject: Injector,
  ) {
    super('app-posting', inject);
    this.createForm();
    this.createSearchForm();
  }

  ngOnInit() {
    this.loadAllPayors();
    this.subscribeObservables();
  }

  loadAllPayors() {
    this.subscriptions.push(
      this.payorService.getPayorsAllPosting().subscribe(
        (resp: any) => {
          if (resp) {
            this.arrayPayors = resp;
          }
        },
        (error) => {
          this.sweetAlertHelper.createCustomAlert({
            title: 'Error',
            text: error.message,
            type: 'error',
          });
        }
      )
    );
  }

  subscribeObservables() {
    this.subscriptions.push(
      this.postingService.batchInfoId.subscribe((batchinfoid: number) => {
        if (batchinfoid && batchinfoid !== null) {
          this.batchinfoid = batchinfoid;
          this.loadBachInfo(batchinfoid);
        }
      })
    );
    this.subscriptions.push(
      this.postingService.arrayBatchcreditid$.subscribe((resp: number[]) => {
        if (resp && resp.length) {
          this.arrayBatchcreditid = resp;
        } else {
          this.arrayBatchcreditid = [];
        }
      })
    );
    this.subscriptions.push(
      this.postingService.reloadBatchData$.subscribe((resp: boolean) => {
        if (resp) {
          if (this.batchinfoid !== null) {
            this.loadBachInfo(this.batchinfoid);
          }
        }
      })
    );

    this.subscriptions.push(
      this.postingService.clearFilterGrid$.subscribe((resp) => {
        if (resp && resp.id === 1) {
          this.disabledFilter = !resp.clear;
        }
      })
    );

    this.subscriptions.push(
      this.postingService.clearFilterGrid$.subscribe((resp) => {
        if (resp && resp.id === 4) {
          this.disabledSorting = !resp.clear;
        }
      })
    );

    this.subscriptions.push(
      this.postingService.enableReadOnlyMode$.subscribe({
        next: (res: boolean) => {
          if (res) {
            this.readOnlyMode = true;
            this.formPostingBatch.disable();
          } else {
            this.readOnlyMode = false;
            this.formPostingBatch.enable();
          }
        },
        error: (error: any) => {
          this.sweetAlertHelper.createCustomAlert({
            title: 'Error',
            text: error.message,
            type: 'error',
          });
        }
      })
    );
  }

  loadBachInfo(batchinfoid: number) {
    if (batchinfoid) {
      this.t2LoadingService.show();
      this.subscriptions.push(
        this.postingService.getBachInfoById(batchinfoid).subscribe(
          (resp: IBatchCreditInfo) => {
            this.t2LoadingService.hiden();
            if (resp) {
              const {
                batchinfoid,
                description,
                check,
                checkdate,
                totaltarget,
                isposting,
                depositdate,
                payorid,
                writeoffamount,
                postedamount,
                revenueadjustments,
                contractualallowance,
                refunds,
                remaining,
                batchstatusid
              } = resp;
              const checkdateFormatted = new Date(checkdate);
              const checkdateFormattedobj = {
                date: {
                  year: checkdateFormatted.getFullYear(),
                  month: checkdateFormatted.getMonth() + 1,
                  day: checkdateFormatted.getDate(),
                }
              };
              const depositdateFormatted = new Date(depositdate);
              const depositdateFormattedobj = {
                date: {
                  year: depositdateFormatted.getFullYear(),
                  month: depositdateFormatted.getMonth() + 1,
                  day: depositdateFormatted.getDate(),
                }
              };
              this.formPostingBatch.patchValue({
                batchinfoid: batchinfoid,
                description: description,
                check: check,
                checkdate: checkdate ? checkdateFormattedobj : null,
                totaltarget: totaltarget ? totaltarget : 0,
                isposting: isposting,
                depositdate: depositdate ? depositdateFormattedobj : null,
                payorid: payorid,
                writeoffamount: writeoffamount,
                postedamount: postedamount,
                revenueadjustments: revenueadjustments,
                contractualallowance: contractualallowance,
                refunds: refunds,
                remaining: remaining,
                batchstatusid: batchstatusid
              });
              resp.batchcredits.forEach((batchcredit: IBatchcredit) => {
                if (batchcredit) {
                  this.batchCredits.push(this.createBatchCredit(batchcredit));
                }
              });
              this.postingService.setBatchCreditListSubject(resp);
              if (isposting) {
                this.postingService.onUpdateRowAmount({ batchInfoId: batchinfoid, totalAmount: totaltarget });
              } else {
                this.postingService.onUpdateRowAmount({ batchInfoId: batchinfoid, totalAmount: postedamount });
              }
            }
          },
          (error) => {
            this.t2LoadingService.hiden();
            this.sweetAlertHelper.createCustomAlert({
              title: 'Error',
              text: error.message,
              type: 'error',
            });
          }
        )
      );
    }
  }

  createForm() {
    this.formPostingBatch = this.fb.group({
      batchinfoid: null,
      description: ['', Validators.required],
      check: ['', Validators.required],
      checkdate: ['', Validators.required],
      totaltarget: [0, Validators.required],
      isposting: [false],
      payorid: ['', Validators.required],
      depositdate: [null, Validators.required],
      postedamount: 0,
      writeoffamount: 0,
      revenueadjustments: 0,
      contractualallowance: 0,
      refunds: 0,
      remaining: 0,
      batchstatusid: null,
      batchcredits: this.fb.array([]),
    });
  }

  createSearchForm() {
    this.searchForm = this.fb.group({
      search: [''],
    });
  }

  createBatchCredit(data: IBatchcredit): FormGroup {
    return this.fb.group({
      batchcreditid: data.batchcreditid,
      batchinfoid: data.batchinfoid,
      billed: data.billed,
      filename: data.filename,
      icn: data.icn,
      mid: data.mid,
      mod: data.mod,
      name: data.name,
      npi: data.npi,
      pnc: data.pnc,
      procd: data.procd,
      qty: data.qty,
      servdate: data.servdate,
    });
  }

  save(): void {
    const formData = Object.assign({}, this.formPostingBatch.value);
    if (formData.checkdate) {
      formData.checkdate = moment(
        getDateFormatted(formData.checkdate.date)
      ).format('MM-DD-YYYY');
    }
    if (formData.depositdate) {
      formData.depositdate = moment(
        getDateFormatted(formData.depositdate.date)
      ).format('MM-DD-YYYY');
    }

    if (this.btnFloppyIsValid) {
      this.sweetAlertHelper
        .createCustomAlert({
          title: 'Error',
          text: 'Please fill all required fields',
          type: 'error'
        })
        .then(() => this.formPostingBatch.touched);
    } else {
      if (formData.batchinfoid) {
        // Put
        delete formData.batchcredits;

        this.postingService.updateBatchInfo(formData)
          .subscribe({
            next: (resp) => {
              this.sweetAlertHelper
                .createCustomAlert({
                  title: 'Success',
                  text: resp.message,
                  type: 'success'
                })
                .then(() => {
                  this.postingService.reloadMainGrid(true);
                });
            },
            error: (error) => {
              this.t2LoadingService.hiden();
              this.sweetAlertHelper.createCustomAlert({
                title: 'Error',
                text: error.message,
                type: 'error'
              });
            }
          });
      } else {
        // save
        formData.batchinfoid = null;
        delete formData.batchcredits;
        this.postingService
          .addPostingBatchCredit(formData)
          .subscribe((resp) => {
              this.sweetAlertHelper
                .createCustomAlert({
                  title: 'Success',
                  text: resp.message,
                  type: 'success'
                })
                .then(() => {
                  this.postingService.reloadMainGrid(true);
                });
            },
            (error: any) => {
              this.t2LoadingService.hiden();
              this.sweetAlertHelper.createCustomAlert({
                title: 'Error',
                text: error.message,
                type: 'error'
              });
            });
      }
    }
  }

  addNewCredit() {
    const searchData = Object.assign({}, this.searchForm.getRawValue());
    this.postingService.onEditBatchCredit(null);
    if (searchData.search) {
      this.t2LoadingService.show();
      const data = { ConsecutiveNumber: searchData.search, BatchInfoId: this.batchinfoid };
      this.subscriptions.push(
        this.postingService
          .searchTrip(data)
          .subscribe(
            (resp: ISeachTrip) => {
              this.t2LoadingService.hiden();
              if (resp) {
                this.postingService.modalChargesDetails.emit({isOpen: true});
                this.postingService.onCreditEdit(resp);
              }
            },
            (error) => {
              this.t2LoadingService.hiden();
              this.sweetAlertHelper.createCustomAlert({
                title: 'Error',
                text: error.message,
                type: 'error',
              });
            }
          )
      );
    } else {
      this.sweetAlertHelper.createCustomAlert({
        title: 'Error',
        text: 'Please enter a reservation number',
        type: 'error',
      });
    }
  }

  deleteCredit() {
    if (this.arrayBatchcreditid.length) {
      this.sweetAlertHelper.createCustomAlert({
        title: 'Delete Record',
        text: 'Are you sure you want to delete this record?',
        type: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, delete it!',
      }).then((result) => {
        if (result.value) {
          this.subscriptions.push(
            this.postingService.postDeleteBatchHistory(this.arrayBatchcreditid).subscribe((resp: any) => {
              this.sweetAlertHelper.createCustomAlert({
                title: 'Success',
                text: resp.message,
                type: 'success',
              }).then(() => {
                this.postingService.reloadBatchData(true);
                this.postingService.setArrayBatchcreditIds([]);
              });
            }, error => {
              this.sweetAlertHelper.createCustomAlert({
                title: 'Error',
                text: error.message,
                type: 'error',
              });
            })
          );
        }
      });
    } else {
      this.sweetAlertHelper.createCustomAlert({
        title: 'Error',
        text: 'Please select a credit to delete',
        type: 'error',
      });
    }
  }

  postingBatch(): void {
    if (!this.btnPostBatchIsValid) {
      const formData = Object.assign({}, this.formPostingBatch.value);
      if (formData.checkdate) {
        formData.checkdate = moment(getDateFormatted(formData.checkdate.date)).format('MM-DD-YYYY');
      }
      if (formData.depositdate) {
        formData.depositdate = moment(getDateFormatted(formData.depositdate.date)).format('MM-DD-YYYY');
      }

      delete formData.batchcredits;
      formData.postedamount = 0;
      this.sweetAlertHelper.createCustomAlert({
        title: 'Posting Batch',
        text: 'Are you sure you want to post this batch?',
        type: 'question',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes',
      }).then((result) => {
        if (result.value) {
          this.t2LoadingService.show();
          this.postingService.postingBatch(formData)
          .subscribe({
            next: (resp: any) => {
              this.t2LoadingService.hiden();
              this.sweetAlertHelper
              .createCustomAlert({
                title: 'Success',
                text: resp.message,
                type: 'success',
              }).then(() => this.postingService.reloadMainGrid(true));
            },
            error: (error) => {
              this.t2LoadingService.hiden();
              this.sweetAlertHelper.captureException(error);
            }
          });
        }
      });
    } else {
      this.sweetAlertHelper
      .createCustomAlert({
        title: 'Forbidden',
        text: '¡Action not allowed!',
        type: 'error',
      }).then(() => this.postingService.reloadMainGrid(true));
    }
  }

  cancel() {
    this.postingService.reloadMainGrid(false);
    this.postingService.setBatchCreditListSubject(null);
    this.postingService.setBatchInfoId(null);
    this.postingService.setArrayBatchcreditIds([]);
  }

  clearFilter() {
    this.postingService.onClearFilterGrid({
      type: 'clearFilter',
      clear: true,
      id: 2
    });
  }
  clearSort() {
    this.postingService.onClearFilterGrid({
      type: 'clearSort',
      clear: true,
      id: 3
    });
  }

  rollBackAction(): void {
    if (!this.isAllowedAction('RollBackBatch')) {
      this.showActionNotAllowedError();
      return;
    }

    this.requestRollbackReason();
  }

  private requestRollbackReason(): void {
    this.sweetAlertHelper.createCustomAlert({
      title: 'Reason for RollBack',
      text: `Please enter the reason for rolling back Batch #${this.batchinfoid}:`,
      input: 'textarea',
      inputPlaceholder: 'Enter reason here',
      inputAttributes: {
        'aria-label': 'Reason for rollback'
      },
      showCancelButton: true,
      confirmButtonText: 'Continue',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      inputValidator: (value) => {
        if (!value || !value.trim().length) {
          return 'You need to provide a reason!';
        }
        if (value.length > 5000) {
          return 'Reason is too long. Maximum 5000 characters allowed.';
        }
        return null;
      }
    }).then((reasonResult) => {
      if (reasonResult.value) {
        this.confirmRollback(reasonResult.value.trim());
      }
    });
  }

  private confirmRollback(reason: string): void {
    this.sweetAlertHelper.createCustomAlert({
      title: 'Apply RollBack in selected Batch',
      text: `Are you sure you want to RollBack the Batch #${this.batchinfoid}?`,
      type: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, rollback!',
    }).then((confirmResult) => {
      if (confirmResult.value) {
        this.executeRollback(reason);
      }
    });
  }

  private executeRollback(reason: string): void {
    if (!this.batchinfoid) {
      return;
    }

    this.t2LoadingService.show();
    this.subscriptions.push(
      this.postingService.rollbackBatch({ 
        batchinfoid: this.batchinfoid,
        reason: reason
      })
      .subscribe({
        next: (res: any) => {
          this.t2LoadingService.hiden();
          if (res && res.success) {
            this.showRollbackSuccess(res.message);
          }
        },
        error: (err: any) => {
          this.t2LoadingService.hiden();
          this.sweetAlertHelper.captureException(err);
        }
      })
    );
  }

  private showRollbackSuccess(message: string): void {
    this.sweetAlertHelper.createCustomAlert({
      title: 'Success',
      text: message,
      type: 'success'
    }).then(() => {
      this.postingService.reloadMainGrid(true);
    });
  }

  private showActionNotAllowedError(): void {
    this.sweetAlertHelper.createCustomAlert({
      title: 'Error',
      text: 'Action not allowed.',
      type: 'error'
    });
  }

  hasPosted(): boolean {
    return this.batchStatusIdCtrl.value === BatchStatusEnum.Posted;
  }

  ngOnDestroy() {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }
}



// WEBPACK FOOTER //
// ./src/app/pages/billing-general/posting/form-posting-batch/form-posting-batch.component.ts